import _ from 'lodash';
import Icon from '@mdi/react';
import {
    mdiContentCopy,
    mdiLinkVariantRemove,
    mdiOpenInNew,
    mdiClipboardMultiple,
    mdiPencil,
    mdiDotsVertical,
    mdiClipboardTextOutline,
    mdiAccountInjuryOutline,
    mdiCalendarBlankOutline,
    mdiCashMultiple,
} from '@mdi/js';
import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { MessagingContext } from '../../../services';
import { formatDate } from '../../../services/primitives/date-utils';
import { BaseButton, Tooltip, CollapsibleContainer, Variables } from '../../base';
import { MessagingPill } from '../messaging-badge';
import { UniversalEntitySearchInput } from '../universal-claims-search-input';
import { createClaimConfig } from '../entity-manager/entity-configs';
import { fetchClaimSummariesFromLatestHl7, IClaimHl7Summary } from '../claim-hl7-summary';
import { SupportClaim, ProviderClaim } from '../../../api/server';
import * as S from './claims-manager.styles';

export interface IClaimsManagerProps {
    messageId?: string;
    claimIds: string[];
    onClaimAdd: (claimId: string) => Promise<void>;
    onBulkClaimsAdd?: (claimIds: string[]) => Promise<void>;
    onClaimRemove: (claimId: string) => Promise<void>;
    className?: string;
}

export const ClaimsManager: React.FunctionComponent<IClaimsManagerProps> = props => {
    const { messageId, claimIds, onClaimAdd, onBulkClaimsAdd, onClaimRemove, className } = props;
    const context = useContext(MessagingContext);
    const { accountId } = useParams<{ accountId: string }>();
    const isSupportApp = context.account?.supportGrant === true;

    const [claims, setClaims] = useState<IClaimHl7Summary[]>([]);
    const [loadingClaims, setLoadingClaims] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [openMenuClaimId, setOpenMenuClaimId] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const pageSize = 5;

    const claimConfig = useMemo(() => {
        return createClaimConfig(isSupportApp, accountId || context.account?.grantedAccountId, context.onClaimSearch);
    }, [isSupportApp, accountId, context.account?.grantedAccountId, context.onClaimSearch]);

    // Stable key so we only refetch when the actual set of claim IDs changes, not when parent passes new array reference (e.g. after markAsRead/markAsUnread)
    const claimIdsKey = useMemo(() => (claimIds?.length ? [...claimIds].sort().join(',') : ''), [claimIds]);

    // Fetch claim details when claimIds change (by value, not reference)
    useEffect(() => {
        const fetchClaims = async () => {
            if (!claimIds || claimIds.length === 0) {
                setClaims([]);
                setLoadingClaims(false);
                return;
            }

            setLoadingClaims(true);
            try {
                // Latest HL7 by created date is the display source of truth (advanced search / claim editor).
                const summaries = await fetchClaimSummariesFromLatestHl7(claimIds, isSupportApp);
                setClaims(summaries);
            } catch (error) {
                console.error('Failed to fetch claims:', error);
            } finally {
                setLoadingClaims(false);
            }
        };

        fetchClaims();
    }, [claimIdsKey, isSupportApp, messageId]);

    // Pagination
    const paginatedClaims = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return claims.slice(startIndex, startIndex + pageSize);
    }, [claims, currentPage]);

    const totalPages = Math.ceil(claims.length / pageSize);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setOpenMenuClaimId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleMenu = (claimId: string) => {
        setOpenMenuClaimId(openMenuClaimId === claimId ? null : claimId);
    };

    const handleClaimSelected = async (claimId: string) => {
        await onClaimAdd(claimId);
        // Reset to first page when adding a new claim
        setCurrentPage(1);
    };

    const handleBulkClaimsSelected = async (claimIds: string[]) => {
        if (onBulkClaimsAdd) {
            try {
                await onBulkClaimsAdd(claimIds);
            } catch (error) {
                console.error('Failed to link claims:', error);
            }
        } else {
            for (const claimId of claimIds) {
                try {
                    await onClaimAdd(claimId);
                } catch (error) {
                    console.error(`Failed to link claim ${claimId}:`, error);
                }
            }
        }
        // Reset to first page after adding claims
        setCurrentPage(1);
    };

    const handleCopyClaimId = (claimId: string) => {
        navigator.clipboard.writeText(claimId);
    };

    const handleCopyAllClaimIds = () => {
        const allClaimIds = claims
            .map(claim => claim.id)
            .filter(id => id)
            .join('\n');
        navigator.clipboard.writeText(allClaimIds);
    };

    const handleNavigateToClaim = (claimId: string) => {
        const accId = accountId || context.account?.grantedAccountId;
        if (accId) {
            window.open(`/${accId}/claims/${claimId}`, '_blank');
        }
    };

    const handleEditClaim = (claimId: string) => {
        const accId = accountId || context.account?.grantedAccountId;
        if (accId) {
            window.open(`/${accId}/claims/${claimId}/edit`, '_blank');
        }
    };

    const handleRemoveClaim = async (claimId: string) => {
        await onClaimRemove(claimId);
        // Adjust page if we deleted the last item on the current page
        if (paginatedClaims.length === 1 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const formatAmount = (amount: IClaimHl7Summary['chargeAmount']): string => {
        if (!amount || !amount.amount) return '$0.00';
        const value = parseFloat(amount.amount);
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: amount.currency || 'USD',
        }).format(value);
    };

    const getPatientName = (claim: IClaimHl7Summary): string => {
        const firstName = claim.patientFirst || '';
        const lastName = claim.patientLast || '';
        return `${firstName} ${lastName}`.trim() || 'Unknown';
    };

    const getTotalContractValue = (): string => {
        const total = claims.reduce((sum, claim) => {
            const value = claim.chargeAmount?.amount ? parseFloat(claim.chargeAmount.amount) : 0;
            return sum + value;
        }, 0);

        const currency = claims[0]?.chargeAmount?.currency || 'USD';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(total);
    };

    return (
        <S.ClaimsContainerWrapper>
            <S.ClaimsCollapsibleContainer
                className={className}
                defaultOpen={false}
                titleComponent={isOpen => {
                    const singleClaimId = !loadingClaims && claims.length === 1 ? claims[0]?.id : undefined;

                    return (
                        <S.SectionHeader>
                            <Icon path={mdiClipboardTextOutline} size={0.6} color={Variables.Colors.n85} />
                            {!isOpen && <S.SectionTitle>Claims</S.SectionTitle>}
                            {!loadingClaims && claims.length > 0 && (
                                <MessagingPill>
                                    {claims.length} - {getTotalContractValue()}
                                </MessagingPill>
                            )}
                            {!isOpen && singleClaimId && (
                                <S.HeaderQuickActionsContainer>
                                    <Tooltip title={<span>Copy Claim ID</span>} direction="top">
                                        <S.ActionButton
                                            aria-label="Copy Claim ID"
                                            onClick={event => {
                                                event.stopPropagation();
                                                handleCopyClaimId(singleClaimId);
                                            }}>
                                            <Icon path={mdiContentCopy} size={0.7} />
                                        </S.ActionButton>
                                    </Tooltip>
                                    <Tooltip title={<span>Open Claim in New Tab</span>} direction="top">
                                        <S.ActionButton
                                            aria-label="Open Claim in New Tab"
                                            onClick={event => {
                                                event.stopPropagation();
                                                handleNavigateToClaim(singleClaimId);
                                            }}>
                                            <Icon path={mdiOpenInNew} size={0.7} />
                                        </S.ActionButton>
                                    </Tooltip>
                                    <Tooltip title={<span>Edit Claim</span>} direction="top">
                                        <S.ActionButton
                                            aria-label="Edit Claim"
                                            onClick={event => {
                                                event.stopPropagation();
                                                handleEditClaim(singleClaimId);
                                            }}>
                                            <Icon path={mdiPencil} size={0.7} />
                                        </S.ActionButton>
                                    </Tooltip>
                                </S.HeaderQuickActionsContainer>
                            )}
                        </S.SectionHeader>
                    );
                }}>
                <S.TabContent>
                    <S.SearchSection>
                        <UniversalEntitySearchInput<SupportClaim | ProviderClaim>
                            entityConfig={claimConfig}
                            onEntitySelected={handleClaimSelected}
                            onBulkEntitiesSelected={handleBulkClaimsSelected}
                            disabled={false}
                        />
                    </S.SearchSection>

                    {loadingClaims ? (
                        <S.LoadingMessage>Loading claims...</S.LoadingMessage>
                    ) : claims.length === 0 ? (
                        <S.EmptyMessage>No claims linked to this message</S.EmptyMessage>
                    ) : claims.length > 0 ? (
                        <>
                            <S.SummarySection>
                                <S.SummaryLabel>
                                    <S.SummaryLabelTitle>Total Claims</S.SummaryLabelTitle>
                                    <S.SummaryLabelValue>{claims.length}</S.SummaryLabelValue>
                                </S.SummaryLabel>
                                <S.SummaryLabel>
                                    <S.SummaryLabelTitle>Contract Value</S.SummaryLabelTitle>
                                    <MessagingPill>{getTotalContractValue()}</MessagingPill>
                                </S.SummaryLabel>
                            </S.SummarySection>
                            <S.Table>
                                <S.TableHead>
                                    <S.TableRow>
                                        <S.TableHeader>
                                            <S.TableHeaderContent>
                                                <Icon
                                                    path={mdiAccountInjuryOutline}
                                                    size={0.6}
                                                    color={Variables.Colors.n85}
                                                />
                                                Patient
                                            </S.TableHeaderContent>
                                        </S.TableHeader>
                                        <S.TableHeader>
                                            <S.TableHeaderContent>
                                                <Icon
                                                    path={mdiCalendarBlankOutline}
                                                    size={0.6}
                                                    color={Variables.Colors.n85}
                                                />
                                                DOS
                                            </S.TableHeaderContent>
                                        </S.TableHeader>
                                        <S.TableHeader className="align-right">
                                            <S.TableHeaderContent>
                                                <Icon path={mdiCashMultiple} size={0.6} color={Variables.Colors.n85} />
                                                Contract Value
                                            </S.TableHeaderContent>
                                        </S.TableHeader>
                                        <S.TableHeader></S.TableHeader>
                                    </S.TableRow>
                                </S.TableHead>
                                <S.TableBody>
                                    {paginatedClaims.map(claim => (
                                        <S.TableRow key={claim.id}>
                                            <S.TableCell className="patient-name">{getPatientName(claim)}</S.TableCell>
                                            <S.TableCell className="dos">
                                                {formatDate(claim.fromDos, 'MMM dd, yyyy')}
                                            </S.TableCell>
                                            <S.TableCell className="value">
                                                {formatAmount(claim.chargeAmount)}
                                            </S.TableCell>
                                            <S.TableCell className="actions">
                                                <S.MenuContainer ref={openMenuClaimId === claim.id ? menuRef : null}>
                                                    <S.MenuButton onClick={() => toggleMenu(claim.id!)}>
                                                        <Icon path={mdiDotsVertical} size={0.58} />
                                                    </S.MenuButton>
                                                    {openMenuClaimId === claim.id && (
                                                        <S.DropdownMenu>
                                                            <S.MenuItem
                                                                onClick={() => {
                                                                    handleCopyClaimId(claim.id!);
                                                                    setOpenMenuClaimId(null);
                                                                }}>
                                                                <Icon path={mdiContentCopy} size={0.7} />
                                                                Copy Claim ID
                                                            </S.MenuItem>
                                                            <S.MenuItem
                                                                onClick={() => {
                                                                    handleEditClaim(claim.id!);
                                                                    setOpenMenuClaimId(null);
                                                                }}>
                                                                <Icon path={mdiPencil} size={0.7} />
                                                                Edit Claim
                                                            </S.MenuItem>
                                                            <S.MenuItem
                                                                onClick={() => {
                                                                    handleNavigateToClaim(claim.id!);
                                                                    setOpenMenuClaimId(null);
                                                                }}>
                                                                <Icon path={mdiOpenInNew} size={0.7} />
                                                                View Claim
                                                            </S.MenuItem>
                                                            <S.MenuItem
                                                                onClick={() => {
                                                                    handleRemoveClaim(claim.id!);
                                                                    setOpenMenuClaimId(null);
                                                                }}>
                                                                <Icon path={mdiLinkVariantRemove} size={0.7} />
                                                                Unlink Claim
                                                            </S.MenuItem>
                                                        </S.DropdownMenu>
                                                    )}
                                                </S.MenuContainer>
                                            </S.TableCell>
                                        </S.TableRow>
                                    ))}
                                </S.TableBody>
                            </S.Table>

                            <S.TableFooter>
                                {totalPages > 1 && (
                                    <S.PaginationContainer>
                                        <S.PaginationInfo>
                                            Showing {(currentPage - 1) * pageSize + 1}-
                                            {Math.min(currentPage * pageSize, claims.length)} of {claims.length}
                                        </S.PaginationInfo>
                                        <S.PaginationControls>
                                            <BaseButton
                                                variant="outlined"
                                                disabled={currentPage === 1}
                                                onClick={() => setCurrentPage(currentPage - 1)}>
                                                Previous
                                            </BaseButton>
                                            <S.PageInfo>
                                                {currentPage} of {totalPages}
                                            </S.PageInfo>
                                            <BaseButton
                                                variant="outlined"
                                                disabled={currentPage === totalPages}
                                                onClick={() => setCurrentPage(currentPage + 1)}>
                                                Next
                                            </BaseButton>
                                        </S.PaginationControls>
                                    </S.PaginationContainer>
                                )}
                                <Tooltip title={<span>Copy All Claim IDs</span>} direction="top">
                                    <S.CopyAllButton onClick={handleCopyAllClaimIds}>
                                        <Icon path={mdiClipboardMultiple} size={0.65} />
                                    </S.CopyAllButton>
                                </Tooltip>
                            </S.TableFooter>
                        </>
                    ) : null}
                </S.TabContent>
            </S.ClaimsCollapsibleContainer>
        </S.ClaimsContainerWrapper>
    );
};
